import React from 'react';
import { CustomFieldInput } from '../../../../types/custom_field';
import { TranslationKey } from '../../../../types/translation/TranslationKey';
import { translate } from '../../../../types/translation/Translator';
import { Badge } from '../../../components/Badge';
import { Checkbox } from '../../../components/Checkbox';
import { Feedback } from '../../../components/Feedback/feedback';
import { TextInput } from '../../../components/TextInput';

type OffsetType = 'before' | 'after' | 'dayOf';

interface CustomFieldDateDefaultValueProps {
  input: CustomFieldInput;
  setInput: React.Dispatch<React.SetStateAction<CustomFieldInput>>;
}

export function CustomFieldDateDefaultValue({ input, setInput }: CustomFieldDateDefaultValueProps) {
  const parsed = input.defaultValue ? parseInt(input.defaultValue) : undefined;

  const getInitialOffsetType = (): OffsetType => {
    if (parsed === undefined) return 'dayOf';
    if (parsed < 0) return 'before';
    if (parsed > 0) return 'after';
    return 'dayOf';
  };

  const [offsetType, setOffsetType] = React.useState<OffsetType>(getInitialOffsetType());
  const [useDefaultValue, _setUseDefaultValue] = React.useState(!!input.defaultValue);
  const setUseDefaultValue = (value: boolean) => {
    setOffsetType('dayOf');
    setInput(input => input.withDefaultValue(value ? '0' : ''));
    _setUseDefaultValue(value);
  };

  return (
    <div className='grid gap-2'>
      <Checkbox
        label={translate({ key: TranslationKey.useDefaultValue })}
        tooltip={translate({ key: TranslationKey.customFieldUseDefaultValueTooltip })}
        value={useDefaultValue}
        onChange={setUseDefaultValue}
      />
      {useDefaultValue && (
        <>
          <div className='flex gap-2'>
            <Badge
              onClick={() => {
                setOffsetType('before');
                if (!parsed) return setInput(input => input.withDefaultValue('-1'));
                if (parsed > 0) setInput(input => input.withDefaultValue((parsed * -1).toString()));
              }}
              selected={offsetType === 'before'}
              text={translate({ key: TranslationKey.before })}
            />
            <Badge
              onClick={() => {
                setOffsetType('dayOf');
                setInput(input => input.withDefaultValue('0'));
              }}
              selected={offsetType === 'dayOf'}
              text={translate({ key: TranslationKey.dayOf })}
            />
            <Badge
              onClick={() => {
                setOffsetType('after');
                if (!parsed) return setInput(input => input.withDefaultValue('1'));
                if (parsed < 0) setInput(input => input.withDefaultValue(Math.abs(parsed).toString()));
              }}
              selected={offsetType === 'after'}
              text={translate({ key: TranslationKey.after })}
            />
          </div>
          {offsetType !== 'dayOf' && (
            <TextInput
              type={'number'}
              value={parsed !== undefined ? Math.abs(parsed) : ''}
              placeholder={translate({ key: TranslationKey.day, count: 'plural' })}
              onChange={value => {
                const parsed = parseInt(value);
                if (!parsed) return setInput(input => input.withDefaultValue(undefined));
                return setInput(input =>
                  input.withDefaultValue(offsetType === 'before' ? `-${parsed}` : parsed.toString()),
                );
              }}
              feedback={value => {
                if (value === '' || value === undefined) {
                  return {
                    type: Feedback.Type.error,
                    text: translate({ key: TranslationKey.customFieldDateDefaultNotDefinedError }),
                  };
                } else {
                  return {
                    type: Feedback.Type.success,
                  };
                }
              }}
            />
          )}
        </>
      )}
    </div>
  );
}
